/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { IDetailsList } from '@fluentui/react/lib/DetailsList';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisText } from 'src/core/components/LuisText';
import { TablePagination } from 'src/core/components/TablePagination';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { UnSubmittedChangesPrompt } from 'src/core/components/UnsubmittedChangesPrompt';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { QnaMakerSynonymCommandBar } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymCommandBar';
import { QnaMakerSynonymTable } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymTable';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import { QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerSynonymStore } from 'src/qnaMaker/core/stores/QnaMakerSynonymStore';
import { useQnaPaginatedAssets } from 'src/qnaMaker/modules/hooks/useQnaPaginatedAssets';
import { useSynonymsActions } from 'src/qnaMaker/modules/hooks/useSynonymsActions';

const messages = defineMessages({
    emptyStateTitle: {
        id: 'qnaMaker.qnaMakerEditSynonyms.emptyStateTitle',
        defaultMessage: 'Add synonyms to your knowledge base'
    },
    emptyStateMessage: {
        id: 'qnaMaker.qnaMakerEditSynonyms.emptyStateMessage',
        defaultMessage: 'Add synonym groups for words that might come up when querying your knowledge base'
    },
    deleteSynonymsModalTitle: {
        id: 'qnaMaker.qnaMakerEditSynonyms.deleteSynonymsModalTitle',
        defaultMessage: 'Delete selected synonym group(s)'
    },
    deleteSynonymsModalBody: {
        id: 'qnaMaker.qnaMakerEditSynonyms.deleteSynonymsModalBody',
        defaultMessage: 'Are you sure you want to delete the selected synonym group(s)?'
    },
    importModalTitle: {
        id: 'qnaMaker.qnaMakerEditSynonyms.importModalTitle',
        defaultMessage: 'Import Synonym Groups'
    },
    unsavedChangesWarningMsg: {
        id: 'qnaMaker.qnaMakerEditSynonyms.unsavedChangesWarningMsg',
        defaultMessage: 'You have unsaved changes. Click on Save changes in the command bar to save your recent work.'
    },
    unSubmittedChangesWarningMsg: {
        id: 'qnaMaker.qnaMakerEditSynonyms.unSubmittedChangesWarningMsg',
        defaultMessage: "You can't save changes since you have edits in progress."
    },
    reviewUnSubmittedChanges: {
        id: 'qnaMaker.qnaMakerEditSynonyms.reviewUnSubmittedChanges',
        defaultMessage: 'Review edits in progress and submit or discard them to proceed.'
    },
    reviewUnSubmittedChangesAnnouncer: {
        id: 'qnaMaker.qnaMakerEditSynonyms.reviewUnSubmittedChangesAnnouncer',
        defaultMessage:
            "You can't save changes since you have edits in progress. Review edits in progress and submit or discard them to proceed."
    },
    synonymGroupCountWithoutFiltering: {
        id: 'qnaMaker.qnaMakerEditSynonyms.synonymGroupCountWithoutFiltering',
        defaultMessage: '{totalCount} synonym groups'
    },
    synonymGroupCountWithFiltering: {
        id: 'qnaMaker.qnaMakerEditSynonyms.synonymGroupCountWithFiltering',
        defaultMessage: '{filteredCount} of {totalCount} synonym groups'
    }
});

export const QnaMakerEditSynonyms = observer(() => {
    const { localize } = useLocalization();

    const uiStore = useLocalStore<QnaMakerSynonymsUiStore>(() => new QnaMakerSynonymsUiStore());

    const { rootStore, dispatcher } = useGlobalState();

    const [pageChangeTrigger, setPageChangeTrigger] = React.useState(getId());
    const isAnnouncedWarningMessageActive = React.useRef(true);
    const [isAddHasSynonyms, setIsAddHasSynonyms] = React.useState(false);

    const { selectedItems, selection } = useSelection<QnaMakerSynonymStore>();

    const {
        searchQuery,
        SynonymsJson,
        isAddSynonymOn,
        savingState,
        displayedSynonymStores,
        synonymsBeingEditedId,
        deleteModalSettings,
        importProjectModalSettings
    } = uiStore;

    const { synonymsJsonData, areSynonymsLoaded, name: currentProjectName, id: currentProjectId } = rootStore.currentQnaMakerProjectStore;

    const { serviceName } = getPortalFromUrl();
    const synonymsFetchingTracker = useProgressTracker();
    const isTableLoading = synonymsFetchingTracker.isRunning || !areSynonymsLoaded;

    const tableRef = React.useRef<IDetailsList>();

    const isInEmptyState: boolean = !isTableLoading && !uiStore.SynonymsJson.length && !isAddSynonymOn;

    const filteredSynonyms = React.useMemo(
        () => orderBy(substringSearchStrategy(displayedSynonymStores, searchQuery, ['alterations']), ['id'], ['desc']),

        [searchQuery, SynonymsJson, pageChangeTrigger, displayedSynonymStores, synonymsBeingEditedId]
    );

    const { items, currentPage, pageCount, onPageChange } = useQnaPaginatedAssets({
        pageSize: LuisConstants.QnaMaker.NumberOfDisplayedQnaPairsPerPage,
        assets: SynonymsJson,
        assetsType: 'synonyms'
    });

    const { editSynonym, saveChanges, addSynonym, deleteSynonym, exportSynonyms, importSynonyms, savingTracker } = useSynonymsActions(
        uiStore
    );

    const handlePageChange = (pageNumber: number) => {
        onPageChange(pageNumber);
        setPageChangeTrigger(getId());
        selection.setAllSelected(false);
        tableRef.current?.focusIndex(0);
    };

    const handleSubmitEditSynonym = (id: number, newData: Partial<QnaMakerSynonymSwagger>) => {
        editSynonym(id, newData);
        if (synonymsBeingEditedId === id) {
            uiStore.clearSynonymsBeingEditedId();
        }
    };

    const deleteModalRenderer = () => {
        const title = localize(messages.deleteSynonymsModalTitle);
        const body = localize(messages.deleteSynonymsModalBody);

        return (
            <DeletePromptModal
                dialogContentProps={{ title }}
                hidden={deleteModalSettings.hidden}
                onDelete={() => {
                    deleteSynonym(selectedItems.map(selectedItem => selectedItem.id));
                    selection.setAllSelected(false);
                }}
                onDismiss={() => {
                    uiStore.setDeleteModalSettings({ hidden: true, operation: null });
                    selection.setAllSelected(false);
                }}>
                {body}
            </DeletePromptModal>
        );
    };

    const emptyStateRenderer = () => (
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack.Item align="center">
                <LuisText fontWeight="bold">{localize(messages.emptyStateTitle)}</LuisText>
            </Stack.Item>
            <Stack.Item align="center">
                <LuisText>{localize(messages.emptyStateMessage)}</LuisText>
            </Stack.Item>
        </Stack>
    );

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerAssets', { tracker: synonymsFetchingTracker, assetsType: 'synonyms' });

        return () => {
            rootStore.currentQnaMakerProjectStore?.resetSynonymsLoadedState();
        };
    }, []);

    React.useEffect(() => {
        if (savingTracker.isFailed) {
            uiStore.setSavingState('dirty');
        }
    }, [savingTracker.isFailed]);

    React.useEffect(() => {
        uiStore.setSynonymsJson(synonymsJsonData);
    }, [synonymsJsonData]);

    React.useEffect(() => {
        uiStore.setDisplayedSynonymStores(items as QnaMakerSynonymStore[]);
    }, [items]);

    React.useEffect(() => {
        isAnnouncedWarningMessageActive.current = savingState !== 'dirty';
    }, [savingState]);

    const handleSaveChanges = () => {
        logButtonClick(serviceName, 'EditKb', 'SaveChanges', 'Synonyms', currentProjectId);
        saveChanges(true);
    };

    const handleAddSynonym = () => {
        logButtonClick(serviceName, 'EditKb', 'AddSynonym', 'Synonyms', currentProjectId);
        uiStore.setIsAddSynonymOn(true);
    };

    const handleImport = (fileType: 'tsv' | 'xlsx') => {
        logButtonClick(serviceName, 'EditKb', fileType === 'xlsx' ? 'ImportExcel' : 'ImportTsv', 'Synonyms', currentProjectId);
        uiStore.setImportProjectModalSettings({ hidden: false, fileType });
    };

    const handleDeleteSynonym = () => {
        logButtonClick(serviceName, 'EditKb', 'Delete', 'Synonyms', currentProjectId);
        uiStore.setDeleteModalSettings({ hidden: false, operation: 'synonym' });
    };

    const handleEditSynonym = () => {
        logButtonClick(serviceName, 'EditKb', 'Edit', 'Synonyms', currentProjectId);
        selectedItems.map(sI => uiStore.setSynonymsBeingEditedId(sI.id));
    };

    return (
        <>
            <UnsavedChangesPrompt
                isPageDirty={savingState === 'dirty'}
                promptPath={`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=synonyms`}
            />
            <UnSubmittedChangesPrompt
                isPageDirty={Boolean(uiStore.synonymsBeingEditedId)}
                promptPath={`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=synonyms`}
            />

            <Stack tokens={{ childrenGap: 22 }} verticalFill>
                <QnaMakerSynonymCommandBar
                    onExport={exportSynonyms}
                    searchQuery={searchQuery}
                    isLoading={isTableLoading}
                    selectedItems={selectedItems}
                    synonymsCountText={
                        searchQuery
                            ? localize(messages.synonymGroupCountWithFiltering, {
                                  filteredCount: filteredSynonyms.length,
                                  totalCount: SynonymsJson.length
                              })
                            : localize(messages.synonymGroupCountWithoutFiltering, { totalCount: SynonymsJson.length })
                    }
                    isAddSynonymOn={isAddSynonymOn}
                    onSave={handleSaveChanges}
                    onAddNewSynonym={handleAddSynonym}
                    isInDirtyState={savingState !== 'saved'}
                    isInEmptyState={uiStore.SynonymsJson.length === 0}
                    onChangeSearchQuery={q => uiStore.setSearchQuery(q)}
                    areEditActionsDisabled={uiStore.savingState === 'inProgress'}
                    isEditSynonymOn={Boolean(synonymsBeingEditedId)}
                    onImport={handleImport}
                    onDelete={handleDeleteSynonym}
                    onEdit={handleEditSynonym}
                />

                {Boolean(synonymsBeingEditedId) || (isAddSynonymOn && isAddHasSynonyms) ? (
                    <>
                        <MessageBar
                            isMultiline
                            dismissButtonAriaLabel="Close"
                            styles={{ root: { width: '100%' } }}
                            messageBarType={MessageBarType.warning}>
                            {localize(messages.unSubmittedChangesWarningMsg)}
                            &nbsp;
                            {!displayedSynonymStores.find(s => synonymsBeingEditedId === s.id) && (
                                <>{localize(messages.reviewUnSubmittedChanges)}</>
                            )}
                        </MessageBar>
                        <Announced aria-live="assertive" message={localize(messages.reviewUnSubmittedChangesAnnouncer)} />
                    </>
                ) : (
                    savingState === 'dirty' && (
                        <>
                            <MessageBar
                                isMultiline={false}
                                dismissButtonAriaLabel="Close"
                                styles={{ root: { width: '100%' } }}
                                messageBarType={MessageBarType.warning}>
                                {localize(messages.unsavedChangesWarningMsg)}
                            </MessageBar>
                            <Announced aria-live="assertive" message={localize(messages.unsavedChangesWarningMsg)} />
                        </>
                    )
                )}

                {isInEmptyState ? (
                    <EmptyStateView message={emptyStateRenderer()} />
                ) : (
                    <>
                        <QnaMakerSynonymTable
                            selection={selection}
                            isAddSynonymOn={isAddSynonymOn}
                            onAddSynonym={value => setIsAddHasSynonyms(value)}
                            componentRef={tableRef}
                            synonymsStore={uiStore}
                            synonymsBeingEditedId={synonymsBeingEditedId}
                            items={filteredSynonyms}
                            overallItemsCount={filteredSynonyms.length}
                            onSubmitAddSynonym={addSynonym}
                            onSubmitEditSynonym={handleSubmitEditSynonym}
                            isLoading={isTableLoading}
                            onDismissAddSynonym={() => uiStore.setIsAddSynonymOn(false)}
                            onDismissEditSynonym={() => uiStore.clearSynonymsBeingEditedId()}
                        />
                        <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => handlePageChange(p.selected)} />
                    </>
                )}

                {deleteModalRenderer()}

                <ImportModal
                    isNameFieldDisplayed={false}
                    hidden={importProjectModalSettings.hidden}
                    fileType={importProjectModalSettings.fileType}
                    dialogContentProps={{ title: localize(messages.importModalTitle) }}
                    onDismiss={() => uiStore.setImportProjectModalSettings({ hidden: true })}
                    onSubmit={data => {
                        importSynonyms({ ...data, fileType: importProjectModalSettings.fileType });
                        uiStore.setImportProjectModalSettings({ hidden: true });
                    }}
                />
            </Stack>
        </>
    );
});
